<template>
  <div>
    <!-- Lexis Nexis check and confirm-->
    <BaseAlert
      v-if="isShowReviewAlert && !detailsCardVisible"
      maxWidth="633px"
      color="#fff1e4"
      class="border-warning"
    >
      <div class="d-flex flex-no-wrap">
        <div class="mr-3">
          <v-icon color="#FF9900">mdi-alert</v-icon>
        </div>
        <div>
          <span class="font-weight-bold">Please review before continuing.</span>
          It's important that the property details highlighted below are
          correct. If <span class="font-weight-bold">incorrect</span>, please
          amend before continuing.
        </div>
      </div>
    </BaseAlert>
    <template
      v-if="
        !isAddressEmpty &&
        !showAddressFinderManuallyForm &&
        !isInputFindAddressVisible
      "
    >
      <div class="address-summary__wrapper">
        <h3>Your address</h3>
        <p>{{ addressDataFromStore.InsuredAddressStreet }}</p>
        <p>{{ addressDataFromStore.InsuredAddressDistrict }}</p>
        <p>{{ addressDataFromStore.InsuredAddressTown }}</p>
        <p>{{ addressDataFromStore.InsuredAddressCounty }}</p>
        <p>{{ addressDataFromStore.InsuredAddressPostCode }}</p>
        <a
          href="javascript:"
          class="font-weight-semi-bold mt-2 accent--text text-cursor psdarkgreen--text"
          @click="editAddressManual"
          >Edit</a
        >
      </div>
    </template>

    <template v-if="isInputFindAddressVisible">
      <InputFindProperty
        :class="inputFindPropertyClass"
        className="italic-placeholder"
        label="Find your property"
        :rules="{
          required: {
            fieldLabel: 'Find your property ',
          },
        }"
        :addressForm="addressForm"
        :isShowCardPropertyDetails="detailsCardVisible"
        :description="addressFinderInputDescription"
        :hideDetails="hideDetails"
        :loading="loading"
        @addressSelected="inputFindPropertyAddressSelected"
        @clear="inputFindPropertyClear"
        @toggleManual="toggleManualInput"
      />
    </template>

    <template v-if="showAddressFinderManuallyForm && !initializingAddress">
      <AddressFinderManuallyForm
        class="address-finder__form"
        :addressForm="addressForm"
        @openInputFindProperty="openInputFindPropertyFromManual"
        @saveManual="saveManualForm"
      />
    </template>

    <template v-if="detailsCardVisible">
      <PropertyDetailsCard
        class="property_card__wrapper mb-9"
        :propertyDetails="propertyDetailsAnswer"
        @emitEditButtonValue="editPropertyDetail"
      />
    </template>

    <template v-if="form.propertyDetailsFormVisibility">
      <PropertyDetailsForm
        :propertyDetailsTextForm="propertyDetailsTextForm"
        :warningMessage="lexisNexisFailedRequestMessage"
        :isReviewAlertShowing="isShowReviewAlert"
        class="mb-9 mt-9"
        @update="checkForThirdPartyAnswers"
      />
    </template>

    <v-dialog
      v-if="warningDialogVisible"
      scrollable
      no-click-animation
      v-model="warningDialogVisible"
      :max-width="600"
      :persistent="true"
      content-class="pb-0 dialog-contents__wrapper"
    >
      <DialogContentWarningEditAddressManual
        ref="DialogContentWarningEditAddressManual"
        @toggleAddressFormManually="toggleAddressFormManually"
        @cancel="warningDialogVisible = false"
      >
      </DialogContentWarningEditAddressManual>
    </v-dialog>
  </div>
</template>

<script>
import LexisNexisLookup from "@/Mixins/PropertyDetails/LexisNexisLookup";
import { generateFormattedAddress } from "@/common/addresses";
import { dereference } from "@/common/transforms";
import { isEmpty } from "@/common/utils";
import AddressFinderManuallyForm from "@/components/AddressFinder/AddressFinderManuallyForm";
import InputFindProperty from "@/components/AddressFinder/InputFindProperty.vue";
import PropertyDetailsCard from "@/components/AddressFinder/PropertyDetailsCard.vue";
import PropertyDetailsForm from "@/components/AddressFinder/PropertyDetailsForm.vue";
import BaseAlert from "@/components/BaseComponents/BaseAlert";
import DialogContentWarningEditAddressManual from "@/components/DialogContents/DialogContentWarningEditAddressManual.vue";
import {
  PROCEEDED,
  QUICKQUOTERETRIEVED,
  QUOTESRETRIEVED,
} from "@/constants/common.constants";
import { ADDRESS_FINDER_MANUALLY_FORM } from "@/constants/form.constants";
import { QUESTIONS_MODULE } from "@/store/types/module-types";
import { mapActions, mapGetters, mapState } from "vuex";
import { PROPERTY_DETAILS_INTERFACE_KEY } from "@/constants/form.constants";

export default {
  name: "LayoutAddressSearch",
  components: {
    AddressFinderManuallyForm,
    InputFindProperty,
    PropertyDetailsForm,
    PropertyDetailsCard,
    DialogContentWarningEditAddressManual,
    BaseAlert,
  },
  props: {
    hideDetails: [Boolean, String],
    isShowDialogContentWarningEditAddressManual: {
      type: Boolean,
      default: true,
    },
    inputFindPropertyClass: {
      type: String,
      default: "mb-9",
    },
    lexisNexisFailedRequestMessage: {
      type: String,
    },
    propertyDetailsTextForm: {
      type: Object,
      required: false,
    },
    addressFinderInputDescription: {
      type: String,
      required: false,
    },
  },
  data() {
    return {
      showPropertyDetailsForm: false,
      showPropertyDetailsCard: false,
      addressForm: ADDRESS_FINDER_MANUALLY_FORM,
      loading: false,
      initializingAddress: true,
      warningDialogVisible: false,
      isShowReviewAlert: false,
    };
  },
  mixins: [LexisNexisLookup],
  computed: {
    ...mapState(QUESTIONS_MODULE, [
      "form",
      "lexisNexisResponse",
      "questionMap",
      "quoteResponse",
      "isQuickQuote",
      "hasResetQuickQuotePropertyData",
      "restoringLastStep",
    ]),
    ...mapGetters(QUESTIONS_MODULE, [
      "allQuestions",
      "isSelfServe",
      "propertyDataRequiresEdit",
      "propertyDetailsAnswer",
      "preparedAddress",
    ]),
    detailsCardVisible() {
      return !this.propertyDataRequiresEdit && this.showPropertyDetailsCard;
    },
    thirdPartyDataReceived() {
      return this.lexisNexisResponse !== null;
    },
    addressDataFromStore() {
      return this.preparedAddress ?? {};
    },
    isAddressEmpty() {
      return isEmpty(this.addressDataFromStore);
    },
    showAddressFinderManuallyForm: {
      get() {
        return this.form.showAddressFinderManuallyForm;
      },
      set(value) {
        this.form.showAddressFinderManuallyForm = value;
      },
    },
    isInputFindAddressVisible() {
      return (
        !this.showAddressFinderManuallyForm &&
        !this.detailsCardVisible &&
        !this.form.propertyDetailsFormVisibility
      );
    },
    manualAddressData: {
      get() {
        return this.form.manualAddressData;
      },
      set(value) {
        this.form.manualAddressData = value;
      },
    },
  },
  watch: {
    restoringLastStep() {
      if (this.shouldCheckAndResetData()) {
        this.searchQuickQuoteAddress();
      }
    },
    propertyDetailsAnswer() {
      this.checkForThirdPartyAnswers();
    },
  },
  mounted() {
    this.initAddressForm();
    if (this.shouldCheckAndResetData()) {
      this.searchQuickQuoteAddress();
    }
    this.checkForThirdPartyAnswers();
  },
  methods: {
    ...mapActions(QUESTIONS_MODULE, [
      "setPropertyDataAndSources",
      "setPropertyDetailsFormVisibility",
      "setIsLNErrorMessageEnabled",
      "updateHasResetQuickQuotePropertyData",
    ]),
    editAddressManual() {
      if (this.isShowDialogContentWarningEditAddressManual) {
        this.warningDialogVisible = true;
      } else {
        this.toggleAddressFormManually(false);
      }
    },
    checkForThirdPartyAnswers() {
      const propertyQuestions = [];

      for (const k of PROPERTY_DETAILS_INTERFACE_KEY) {
        propertyQuestions.push(this.questionMap[k]);
      }

      const areSomeAnswersThirdParty = propertyQuestions.some(
        (question) => question && question?.Source === "ThirdParty"
      );

      if (areSomeAnswersThirdParty) {
        this.isShowReviewAlert = true;
        return;
      }

      const areAllAnswersFromUser =
        propertyQuestions.filter(
          (question) =>
            (question && question?.Source === "User") ||
            question?.Source === "Default" ||
            question?.Source === ""
        ).length === PROPERTY_DETAILS_INTERFACE_KEY.length;

      if (areAllAnswersFromUser) {
        this.isShowReviewAlert = false;
        return;
      }
    },
    toggleAddressFormManually(clearAddressData) {
      this.showAddressFinderManuallyForm = true;
      this.warningDialogVisible = false;
      if (clearAddressData) {
        this.setPropertyDataAndSources(null);
        this.clearAddressForm();
      }
    },
    setPropertyDetailsCardVisibility(value) {
      this.showPropertyDetailsCard = value;
    },
    editPropertyDetail() {
      this.setPropertyDetailsCardVisibility(false);
      this.checkForThirdPartyAnswers();
    },
    checkAndSetCardVisibility() {
      this.setPropertyDetailsCardVisibility(this.thirdPartyDataReceived);
    },
    openInputFindPropertyFromManual() {
      this.showAddressFinderManuallyForm = false;
      this.form.propertyDetailsFormVisibility = false;
      this.isShowReviewAlert = false;

      this.clearAddressForm();
      this.setPropertyDataAndSources(null);

      this.setPropertyDetailsCardVisibility(false);
    },
    async inputFindPropertyAddressSelected() {
      // Lexis Nexis lookup - see Mixins for definition
      await this.propertyDetailsLookupRemoveDependenciesByQuestion();
      this.setPropertyDetailsCardVisibility(true);
      this.manualAddressData = dereference(this.addressForm);
    },
    inputFindPropertyClear() {
      if (this.loading) {
        // if is loading, i.e loading lexis nexis, we don't actually clear anything, just let the data load after being fetch like usual
        return;
      }
      this.setPropertyDataAndSources(null);
      this.clearAddressForm();
      this.form.isLNErrorMessageEnabled = true;
      this.setPropertyDetailsCardVisibility(false);
      this.setPropertyDetailsFormVisibility(false);
    },
    toggleManualInput() {
      this.clearAddressForm();
      this.toggleAddressFormManually(true);
      this.setPropertyDetailsCardVisibility(false);
    },
    clearAddressForm() {
      this.addressForm.findProperty.Value = "";
      this.addressForm.postCode.Value = "";
      this.addressForm.street.Value = "";
      this.addressForm.district.Value = "";
      this.addressForm.town.Value = "";
      this.addressForm.county.Value = "";
    },
    saveManualForm(addressData) {
      this.saveAddressToStorage(addressData);
      this.showAddressFinderManuallyForm = false;
      this.setPropertyDetailsFormVisibility(true);
    },
    /**
     *
     * @param {{postCode: String, street: String, district: String, town: String, county: String}} addressData
     */
    saveAddressToStorage(addressData) {
      // remove duplicate address values
      addressData.county =
        addressData.town === addressData.county ? "" : addressData.county;

      this.addressForm.postCode.Value = addressData.postCode;
      this.addressForm.street.Value = addressData.street;
      this.addressForm.district.Value = addressData.district;
      this.addressForm.town.Value = addressData.town;
      this.addressForm.county.Value = addressData.county;
    },
    initAddressForm() {
      this.addressForm = {
        findProperty: this.questionMap.InsuredFindProperty,
        postCode: this.questionMap.InsuredAddressPostCode,
        street: this.questionMap.InsuredAddressStreet,
        district: this.questionMap.InsuredAddressDistrict,
        town: this.questionMap.InsuredAddressTown,
        county: this.questionMap.InsuredAddressCounty,
      };

      const address = generateFormattedAddress(this.addressForm);

      this.form.propertyDetailsFormVisibility = !!address;
      if (address && !this.addressForm.findProperty.Value) {
        this.addressForm.findProperty.Value = address;
      }

      if (!this.manualAddressData || isEmpty(this.manualAddressData)) {
        this.manualAddressData = dereference(this.addressForm);
      }
      this.initializingAddress = false;
    },
    shouldCheckAndResetData() {
      return (
        this.isQuickQuote &&
        this.isSelfServe &&
        // only search after step has settled
        !this.restoringLastStep &&
        !this.hasResetQuickQuotePropertyData &&
        this.isAddressValid() &&
        this.quoteResponse.QuoteStatus === QUOTESRETRIEVED &&
        this.quoteResponse.QuickQuoteStatus &&
        (this.quoteResponse.QuickQuoteStatus === QUICKQUOTERETRIEVED ||
          this.quoteResponse.QuickQuoteStatus === PROCEEDED)
      );
    },
    isAddressValid() {
      const addressForm = this.addressForm;
      return (
        addressForm.postCode.Value &&
        addressForm.street.Value &&
        addressForm.town.Value
      );
    },
    searchQuickQuoteAddress() {
      // required to hide when searching value to reset state of other child components PropertyDetailsForm
      // so that we don't have bug related to inner state of those component
      this.setPropertyDetailsFormVisibility(false);
      this.setPropertyDetailsCardVisibility(false);
      // use setTimeout here so that the child component load first before we run find property and set loading = true
      // if we don't use setTimeout here, child component will be initialized with loading = true
      // => the input find property not update its internal loading state and show that it's loading data from the Api
      setTimeout(async () => {
        await this.propertyDetailsLookupRemoveDependenciesByQuestion();
        Object.entries(this.form.originalQuickQuotePropertyData).forEach(
          ([key, value]) => {
            if (this.questionMap[key]) {
              this.questionMap[key].Value = value;
            }
          }
        );
        this.setPropertyDetailsFormVisibility(true);
        this.updateHasResetQuickQuotePropertyData(true);
      }, 0);
    },
  },
};
</script>

<style lang="scss" scoped>
.address-summary__wrapper {
  border-left: solid 2px #106175;
  padding-left: 24px;
  margin-bottom: 24px;

  p,
  h4 {
    margin: 0;
    font-size: 18px;
    color: $black-2;
  }
}
.property_card__wrapper {
  max-width: 633px;
}
:deep(.property_type__wrapper) {
  max-width: 675px;
  .v-input--radio-group__input {
    .v-btn {
      border-radius: 15px !important;
    }
  }
}
:deep(.find-property) {
  max-width: 480px;
  .v-input__slot {
    min-height: 48px !important;
  }
}
@media screen and (max-width: $grid-breakpoints-sm) {
  :deep(.find-property) {
    .v-input__slot {
      min-height: 60px !important;
    }
  }
}
</style>
